import useGetCurrentConditions from '@/Components/Hooks/useGetCurrentConditions';
import { DEVICE_PRODUCT_CLASS, DEVICE_EVENT_TYPES } from '@/constants/device';
import { apiCallback } from '@vantiva-sa/homesight_humaui/Services/Common';
import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';
import { get, groupBy, isEmpty } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useEventStream } from '../Hooks/useEventStream';
import {
  checkAllDevicesProcessed,
  envEventTypes,
  filterEventsData,
  postalCodeStatus as postalcodeStatusConst,
  processEventFromStream,
  processWeatherData,
  reduceEventsData,
} from './EnvironmentalUtils';

export const useUserAccessories = (hubRecord) => {
  const [userAccessoriesDeviceuuid, setUserAccessoriesDeviceuuid] = useState(null);
  const [sensors, setSensors] = useState([]);
  const userEventsApi = useSelector((state) => state.apis.userEventsApi);
  const trans = useSelector(translator);

  useEffect(() => {
    const fetchUserAccessories = () => {
      const errorCallback = (error) => {
        console.error(error);
      };
      const successCallback = (data) => {
        const deviceUuids = data['user_accessories']
          .filter((device) => device?.productclass === DEVICE_PRODUCT_CLASS.ENVIRONMENTAL_SENSOR)
          .sort((a, b) => {
            return a?.location?.localeCompare(b?.location);
          })
          .reduce((acc, device) => {
            acc[device.deviceuuid] = {
              location: device.location,
              connectionStatus: device.device_connected ? 'CONNECTED' : 'DISCONNECTED',
              batteryLevel: device.device_battery_level,
              pairedStatus: device?.paired_status !== 'UNPAIRED',
              serialNumber: device.device_serial_number,
              devicetype: device.devicetype,
              deviceuuid: device.deviceuuid,
            };
            return acc;
          }, {});
        setUserAccessoriesDeviceuuid(deviceUuids);

        const sensors = data['user_accessories']
          .filter((device) => device?.productclass === DEVICE_PRODUCT_CLASS.SMART_HOME)
          .sort((a, b) => {
            // First sort by devicetype
            if (a.devicetype < b.devicetype) return -1;
            if (a.devicetype > b.devicetype) return 1;

            // If devicetype is the same, sort alphabetically by location
            return a?.location?.localeCompare(b?.location);
          });
        const groupedSensors = groupBy(sensors, 'devicetype');

        setSensors(groupedSensors);
      };

      try {
        userEventsApi.getUserAccessories(
          hubRecord.useruuid,
          { eventtypes: [] },
          apiCallback({
            translator: trans,
            failCallback: errorCallback,
            errorCallback,
            successCallback,
          })
        );
      } catch (error) {
        errorCallback(error);
      }
    };

    fetchUserAccessories();
  }, [hubRecord, userEventsApi, trans]);

  return { userAccessoriesDeviceuuid, sensors };
};

export const useUserEvents = (hubRecord, userAccessoriesDeviceuuid, setUserEventsData) => {
  const userEventsApiFetchingStateRef = useRef({});

  const [dataProcessed, setDataProcessed] = useState(false);

  const userEventsApi = useSelector((state) => state.apis.userEventsApi);
  const trans = useSelector(translator);

  const processUserEventsData = useCallback(
    (deviceuuid, eventsData) => {
      setUserEventsData((currentData) => {
        const updatedData = {
          ...currentData,
          [deviceuuid]: {
            data: {},
            location: get(userAccessoriesDeviceuuid, `${deviceuuid}.location`, ''),
          },
        };

        if (!isEmpty(eventsData)) {
          const filteredEventsData = filterEventsData(eventsData);
          if (!isEmpty(filteredEventsData)) {
            const reducedEventsData = reduceEventsData(filteredEventsData);
            reducedEventsData['deviceId'] = get(
              userAccessoriesDeviceuuid,
              `${deviceuuid}.serialNumber`,
              ''
            );
            updatedData[deviceuuid]['data'] = reducedEventsData;
          }
        }

        if (checkAllDevicesProcessed(updatedData, userAccessoriesDeviceuuid)) {
          setDataProcessed(true);
        }

        return updatedData;
      });
    },
    [setUserEventsData, userAccessoriesDeviceuuid]
  );

  useEffect(() => {
    userEventsApiFetchingStateRef.current = {};
  }, []);

  useEffect(() => {
    if (userAccessoriesDeviceuuid) {
      Object.keys(userAccessoriesDeviceuuid).forEach((deviceuuid) => {
        const fetchLatestUserEvents = (deviceuuid) => {
          const errorCallback = (error) => {
            console.error(error);
            processUserEventsData(deviceuuid, []);
          };
          const successCallback = (data) => {
            processUserEventsData(deviceuuid, data['user_events']);
          };

          try {
            userEventsApi.getLatestUserEvents(
              hubRecord.useruuid,
              {
                deviceuuid: [deviceuuid],
                // eventtypes: ['environment_status']
                eventtypes: [...Object.values(envEventTypes)],
              },
              apiCallback({
                translator: trans,
                failCallback: errorCallback,
                errorCallback,
                successCallback,
              })
            );
            userEventsApiFetchingStateRef.current[deviceuuid] = true;
          } catch (error) {
            errorCallback(error);
          }
        };
        if (!userEventsApiFetchingStateRef.current[deviceuuid]) {
          fetchLatestUserEvents(deviceuuid);
        }
      });
      if (!Object.keys(userAccessoriesDeviceuuid).length) {
        setDataProcessed(true);
      }
    }
  }, [hubRecord.useruuid, trans, userAccessoriesDeviceuuid, userEventsApi, processUserEventsData]);

  return { dataProcessed };
};

export const useEnvironmentalEventStream = (
  userInfo,
  userEventsData,
  userAccessoriesDeviceuuid,
  timezone,
  setUserEventsData
) => {
  const { latestEvent } = useEventStream(userInfo, [
    ...Object.values(envEventTypes),
    ...Object.values(DEVICE_EVENT_TYPES).flat(),
  ]);

  useEffect(() => {
    if (!latestEvent || isEmpty(userEventsData) || isEmpty(userAccessoriesDeviceuuid)) return;
    processEventFromStream(
      latestEvent,
      userEventsData,
      userAccessoriesDeviceuuid,
      !isEmpty(timezone) ? timezone.split(' ')[0] : 'UTC',
      setUserEventsData
    );
  }, [latestEvent, userEventsData, userAccessoriesDeviceuuid, timezone, setUserEventsData]);

  return { latestEvent };
};

export const useWeatherData = (userInfo, hubRecord, tempUnit) => {
  const [outdoorWeather, setOutdoorWeather] = useState({});
  const [postalcodeStatus, setpostalcodeStatus] = useState(null);
  const [outdoorWeatherFetched, setOutDoorWeatherFetched] = useState(false);

  const {
    data: currentConditions,
    isLoading,
    isError,
    error,
    isSuccess,
  } = useGetCurrentConditions({
    useruuid: hubRecord.useruuid,
    postalcode: userInfo.postalcode,
    city: userInfo.city,
  });

  if (!userInfo.postalcode && !userInfo.city && postalcodeStatus !== postalcodeStatusConst.empty) {
    setpostalcodeStatus(postalcodeStatusConst.empty);
    setOutDoorWeatherFetched(true);
  }

  useEffect(() => {
    if (
      postalcodeStatus !== postalcodeStatusConst.invalid &&
      isError &&
      error?.response?.body?.info?.message?.includes(postalcodeStatusConst.noLocationKey)
    ) {
      setpostalcodeStatus(postalcodeStatusConst.invalid);
    } else if (isSuccess && postalcodeStatus !== postalcodeStatusConst.valid) {
      processWeatherData(currentConditions, tempUnit, setOutdoorWeather);
      setpostalcodeStatus(postalcodeStatusConst.valid);
    }
    if (!outdoorWeatherFetched && !isLoading && (isSuccess || isError)) {
      setOutDoorWeatherFetched(true);
    }
  }, [
    currentConditions,
    error,
    isError,
    isLoading,
    isSuccess,
    outdoorWeatherFetched,
    postalcodeStatus,
    tempUnit,
  ]);

  return {
    postalcodeStatus,
    outdoorWeather,
    outdoorWeatherFetched,
    isLoading,
    isError,
    isSuccess,
  };
};
